import { useState, useEffect, useRef } from "react";

interface TriggerInputProps {
  initialValue: string;
  onSave: (value: string) => void;
  placeholder?: string;
  className?: string;
  isTextarea?: boolean;
  rows?: number;
  disabled?: boolean;
  validationFn?: (value: string) => boolean;
  showValidation?: boolean;
}

export const TriggerInput = ({
  initialValue,
  onSave,
  placeholder = "",
  className = "",
  isTextarea = false,
  rows = 3,
  disabled = false,
  validationFn,
  showValidation = false
}: TriggerInputProps) => {
  // Local state for the input value
  const [value, setValue] = useState(initialValue);
  const [isValid, setIsValid] = useState(true);
  
  // Track if the input is being edited
  const isEditingRef = useRef(false);
  
  // Track if this is the initial render
  const initialRenderRef = useRef(true);

  // Update local state when props change, but only if we're not editing
  useEffect(() => {
    if (!isEditingRef.current && !initialRenderRef.current) {
      setValue(initialValue);
    }
    initialRenderRef.current = false;
  }, [initialValue]);

  // Validate the value when it changes
  useEffect(() => {
    if (validationFn) {
      setIsValid(validationFn(value));
    }
  }, [value, validationFn]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    isEditingRef.current = true;
    setValue(e.target.value);
  };

  const handleBlur = () => {
    if (isEditingRef.current) {
      onSave(value);
      isEditingRef.current = false;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !isTextarea) {
      e.currentTarget.blur();
    } else if (e.key === 'Enter' && e.ctrlKey && isTextarea) {
      e.currentTarget.blur();
    }
  };

  const validationClass = showValidation && validationFn
    ? value && !isValid
      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
      : value
        ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
        : ''
    : '';

  return isTextarea ? (
    <textarea
      value={value}
      onChange={handleChange}
      onFocus={() => { isEditingRef.current = true; }}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`${className} ${validationClass}`}
      placeholder={placeholder}
      rows={rows}
      disabled={disabled}
    />
  ) : (
    <input
      type="text"
      value={value}
      onChange={handleChange}
      onFocus={() => { isEditingRef.current = true; }}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`${className} ${validationClass}`}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
};
